import { legacyTarget } from '../../nucleus/legacy';
import { pathFor } from '../../nucleus/map';
import { fmt } from '../../nucleus/format';

// ACTIVITY'S VOCABULARY — what the ledger's kinds, views and actors are called,
// how its entries fold into runs and days, how a filtered ledger is written in
// the address, and where each thing an entry names lives on the map. Pure, so
// the two pages (Log, Inbox) and their tests share one copy.

// ── the ledger ──────────────────────────────────────────────────────────────
export const KINDS = {
    node: { word: 'Nodes', color: '#5b8fc9' },
    app_access: { word: 'Access & consent', color: '#8fa276' },
    data_read: { word: 'Data reads', color: '#7c8a99' },
    security: { word: 'Security', color: '#d09a2e' },
    error: { word: 'Errors', color: '#d0503f' },
    sharing: { word: 'Sharing', color: '#bd6f86' },
    plugin: { word: 'Plugin runs', color: '#bd7748' },
    automation: { word: 'Automation runs', color: '#7a5aa6' },
    relationship: { word: 'Relations', color: '#5b68a0' },
};
export const KIND_IDS = Object.keys(KINDS);
export const kindOf = (k) => KINDS[k] || KINDS.node;

export const VIEWS = [
    { id: 'all', word: 'Everything' },
    // a person's row can be someone else's now that their reads are logged: Me is yours
    { id: 'me', word: 'Me', entities: ['user'], mine: true },
    // whoever else read or changed it: a friend, a COSM agent, anyone on your public page
    { id: 'people', word: 'People', entities: ['user', 'cosm_agent', 'public'], others: true },
    { id: 'apps', word: 'Apps', entities: ['app'] },
    { id: 'tokens', word: 'Tokens', entities: ['token'] },
    { id: 'delegates', word: 'On my behalf', entities: ['sub_entity', 'automation', 'plugin'] },
    { id: 'writes', word: 'Changes', kinds: ['node', 'relationship', 'sharing'] },
    { id: 'security', word: 'Security', kinds: ['security', 'error'] },
];
// In a group the log is shared: the same questions, asked of everyone in it
// ("By me" is the viewer's own rows, among everyone's people rows).
export const GROUP_VIEWS = [
    { id: 'all', word: 'Everything' },
    { id: 'mine', word: 'By me', entities: ['user'], mine: true },
    { id: 'people', word: 'People', entities: ['user', 'cosm_agent', 'public'] },
    { id: 'apps', word: 'Apps', entities: ['app'] },
    { id: 'tokens', word: 'Tokens', entities: ['token'] },
    { id: 'delegates', word: 'Agents & plugins', entities: ['sub_entity', 'automation', 'plugin'] },
    { id: 'writes', word: 'Changes', kinds: ['node', 'relationship', 'sharing'] },
    { id: 'security', word: 'Security', kinds: ['security', 'error'] },
];
export const viewsFor = (inGroup) => (inGroup ? GROUP_VIEWS : VIEWS);
// what plugins and automations did is part of the log by default, beside what
// people and apps did
export const VIEW_DEFAULT_KINDS = ['node', 'app_access', 'data_read', 'plugin', 'automation', 'security', 'error'];
// a group's log leads with what its people did: members, realms, contracts, settings
export const GROUP_DEFAULT_KINDS = ['node', 'relationship', 'app_access', 'sharing', 'plugin', 'automation', 'security', 'error'];
// A view with its own kinds asks for exactly those; the others ask for your
// saved defaults, else these.
export const viewKinds = (v, defaults = null) => v.kinds || defaults || VIEW_DEFAULT_KINDS;

// Who can act. A delegate — sub-entity, automation, plugin — borrows its parent app's
// authority. A token holds its own contract, as an app does (pages/access/TokenMaker.js).
export const ENTITY_TYPES = ['user', 'group', 'app', 'token', 'sub_entity', 'automation', 'plugin', 'cosm_agent', 'public', 'system'];
export const ENTITY_WORD = {
    user: 'People', group: 'Groups', app: 'Apps', token: 'Tokens', sub_entity: 'Sub-entities',
    automation: 'Automations', plugin: 'Plugins', cosm_agent: 'COSM agents', public: 'Public page', system: 'System',
};
export const ONE_WORD = {
    app: 'App', token: 'Token', sub_entity: 'Sub-entity', automation: 'Automation', plugin: 'Plugin',
    cosm_agent: 'COSM agent', public: 'Public page', group: 'Group', system: 'System',
};
// an actor that holds its own access, through no app: what it did is never "under" one
const HOLDS_ITS_OWN = new Set(['app', 'token', 'cosm_agent', 'public']);
export const inOrder = (set, order) => order.filter((k) => set.has(k));

export const RANGES = [
    { id: 'today', word: 'Today' },
    { id: 'week', word: '7 days' },
    { id: 'month', word: '30 days' },
    { id: 'all', word: 'All time' },
];
export const DAY = 86_400_000;
export function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
export function rangeStart(range, now = new Date()) {
    if (range === 'today') return startOfDay(now);
    if (range === 'week') return new Date(now.getTime() - 7 * DAY);
    if (range === 'month') return new Date(now.getTime() - 30 * DAY);
    return null;
}

// WHAT HAPPENED, IN EVERYDAY WORDS. The log's own labels are the system's
// ("Node Created", "Login Success", "Ledgerly read data via GET /api/v1/nodes"):
// a change to a node says what was done to it; signing in says so; an app's read
// says it read your data (the call itself is in the entry); any other label is
// put in sentence case, with the product's words for its nouns.
const SAID = {
    node_created: 'Created', node_create: 'Created', node_updated: 'Changed', node_update: 'Changed',
    node_deleted: 'Deleted', node_delete: 'Deleted', node_restored: 'Restored', node_restore: 'Restored', node_purged: 'Deleted for good',
    app_node_created: 'Created', app_node_updated: 'Changed', app_node_deleted: 'Deleted',
    login_success: 'Signed in', login_failed: 'Sign-in refused', login_failure: 'Sign-in refused', logout: 'Signed out',
};
// A group's log row about a node the viewer's realm hides keeps who did it and
// what kind of thing they did, and loses what it was about (`redacted`).
const HIDDEN = 'a node you can’t see';
const isNodeAction = (a) => !!NODE_DOINGS[a] || a === 'node_purged' || String(a || '').startsWith('app_node_');
export function plainSummary(e, meId = null, groupName = null) {
    if (e?.redacted) {
        const verb = SAID[e.action] || (NODE_DOINGS[e.action] && NODE_DOINGS[e.action].replace(/^./, (c) => c.toUpperCase()));
        return verb ? `${verb} ${HIDDEN}` : `Something you can’t see`;
    }
    const said = groupDoing(e, meId);
    if (said) return said.charAt(0).toUpperCase() + said.slice(1);
    const subject = e.resource?.title || e.resource?.label;
    const verb = SAID[e.action];
    if (verb) return subject ? `${verb} “${subject}”` : verb;
    if (e.category === 'data_read') {
        const n = Number(e.details?.count) || 0;
        const whose = groupName ? `${groupName}’s` : 'your';
        if (n) return n > 1 ? `Read ${fmt(n)} of ${whose} nodes` : `Read one of ${whose} nodes`;
        if (subject) return `Read “${subject}”`;
        return readOfRoute(e.resource?.endpoint, whose) || `Read ${whose} data`;
    }
    const s = String(e.summary || '');
    const m = s.match(/^([^"]*?)(\s*".*)?$/);
    let head = (m ? m[1] : s).trim();
    const tail = (m && m[2]) || '';
    // only a label in Title Case is the system's own; anything else is already a sentence
    if (/^([A-Z][A-Za-z-]*)(\s+[A-Z][A-Za-z-]*)*$/.test(head)) {
        head = head.replace(/Privacy Realm/g, 'Realm').replace(/Exposure Profile/g, 'Contract');
        head = head.charAt(0) + head.slice(1).toLowerCase();
    }
    return head + tail.replace(/"([^"]*)"/g, '“$1”');
}

// WHAT A READ WAS, from the route it came through: an app's read is logged by route,
// never by node (third_party_api_metrics), so the route is what says it.
const READ_ROUTES = [
    [/\/nodes\/<[^>]+>\/(history|ledger|at-time|versions?)/, () => 'Read a node’s history'],
    [/\/download$/, () => 'Downloaded a file'],
    [/\/nodes\/count$/, (w) => `Counted ${w} nodes`],
    [/\/nodes\/<[^>]+>$/, (w) => `Read one of ${w} nodes`],
    [/\/nodes$/, (w) => `Listed ${w} nodes`],
    [/\/sync/, (w) => `Synced ${w} changes`],
    [/my-permissions$/, () => 'Checked what it may do'],
    [/\/schemas/, () => 'Read the schemas'],
];
export function readOfRoute(endpoint, whose = 'your') {
    if (!endpoint) return null;
    const hit = READ_ROUTES.find(([re]) => re.test(endpoint));
    return hit ? hit[1](whose) : null;
}

// WHAT SOMEONE DID IN A GROUP, as the rest of a sentence// WHAT SOMEONE DID IN A GROUP, as the rest of a sentence ("promoted Ava to
// admin", "made the realm “Editors”"): the group's own actions (log_group_audit,
// backend/app/utils/audit.py) and the realm, app and node writes a group logs.
// Null for anything else. A person named is "you" when it is the viewer.
const q = (s) => (s ? `“${s}”` : null);
function personIn(d, meId) {
    if (meId && d.target_user_id && String(d.target_user_id) === String(meId)) return 'you';
    return d.target_username || d.member_username || d.invitee_username || d.username || null;
}
const DOINGS = {
    member_promoted: (d, me) => `promoted ${personIn(d, me) || 'a member'} to admin`,
    member_demoted: (d, me) => `made ${personIn(d, me) || 'an admin'} a member`,
    member_removed: (d, me) => `removed ${personIn(d, me) || 'a member'} from the group`,
    member_left: () => 'left the group',
    member_realm_assigned: (d, me) => `gave ${personIn(d, me) || 'a member'} the realm ${q(d.assigned_realm_name || d.realm_name) || 'they write with'}`,
    member_realm_cleared: (d, me) => `took ${personIn(d, me) === 'you' ? 'your' : personIn(d, me) ? `${personIn(d, me)}’s` : 'a member’s'} realm away`,
    invitation_sent: (d, me) => `invited ${personIn(d, me) || 'someone'}${d.role === 'admin' ? ' as an admin' : ''}`,
    invitation_cancelled: (d, me) => `cancelled ${personIn(d, me) === 'you' ? 'your' : personIn(d, me) ? `${personIn(d, me)}’s` : 'an'} invitation`,
    invitation_accepted: () => 'joined the group',
    invitation_declined: () => 'declined the invitation',
    contract_paused: (d) => `paused the contract with ${d.name || 'an app'}`,
    contract_resumed: (d) => `resumed the contract with ${d.name || 'an app'}`,
    contract_revoked: (d) => `revoked the contract with ${d.name || 'an app'}`,
    contracts_paused_all: (d) => (typeof d.count === 'number' ? `paused every contract (${d.count})` : 'paused every contract'),
    contracts_resumed_all: (d) => (typeof d.count === 'number' ? `resumed every contract (${d.count})` : 'resumed every contract'),
    group_settings_updated: () => 'changed the group’s settings',
    group_public_page_published: () => 'made the public page',
    group_public_page_removed: () => 'took the public page down',
    group_encryption_enabled: () => 'turned on encryption',
    group_key_rotated: () => 'rotated the group’s key',
    // the group is gone, so its name is said ("sam deleted the group “Book Club”")
    group_deleted: (d) => (d.group_name ? `deleted the group ${q(d.group_name)}` : 'deleted the group'),
    exposure_profile_created: (d, me, e) => `made the realm ${q(d.realm_name || e.resource?.label) || ''}`.trim(),
    exposure_profile_updated: (d, me, e) => (d.shared_node_id ? null : `changed the realm ${q(d.realm_name || e.resource?.label) || ''}`.trim()),
    exposure_profile_deleted: (d, me, e) => `deleted the realm ${q(d.realm_name || e.resource?.label) || ''}`.trim(),
    app_authorized: (d, me, e) => `connected ${d.app_name || e.resource?.label || 'an app'}`,
    app_revoked: (d, me, e) => `disconnected ${d.app_name || e.resource?.label || 'an app'}`,
    // a plugin's run (runner._audit): "ran “Mailroom”", whoever started it
    plugin_execution: (d, me, e) => `ran ${q(d.plugin_name || e.resource?.label) || 'a plugin'}`,
};
const NODE_DOINGS = {
    node_created: 'created', node_create: 'created', node_updated: 'changed', node_update: 'changed',
    node_deleted: 'trashed', node_delete: 'trashed', node_restored: 'restored', node_restore: 'restored',
};
export function groupDoing(e, meId = null) {
    const d = e?.details && typeof e.details === 'object' ? e.details : {};
    const said = DOINGS[e?.action]?.(d, meId, e);
    if (said) return said;
    return null;
}

/** Who did it, in a group's words: "You" for the viewer, else their name. */
export function whoOf(e, meId) {
    if (actorType(e) === 'user' && meId && e.actor?.user_id && String(e.actor.user_id) === String(meId)) return 'You';
    return actorName(e);
}

/** One sentence: who, then what ("Sam made the realm “Editors”", "You promoted Ava to admin"). */
export function sentenceOf(e, meId = null) {
    const who = whoOf(e, meId);
    if (e?.redacted) {
        const done = NODE_DOINGS[e.action] || (e.action === 'node_purged' ? 'deleted' : SAID[e.action]?.toLowerCase());
        if (done && isNodeAction(e.action)) return `${who} ${done} ${HIDDEN}`;
        return `${who} did something you can’t see`;
    }
    const doing = groupDoing(e, meId);
    if (doing) return `${who} ${doing}`;
    const node = NODE_DOINGS[e?.action];
    const subject = e?.resource?.title || e?.resource?.label;
    if (e?.action === 'node_purged') return subject ? `${who} deleted “${subject}” for good` : `${who} deleted a node for good`;
    if (node) return subject ? `${who} ${node} “${subject}”` : `${who} ${node} a node`;
    const verb = SAID[e?.action];
    if (verb) return subject ? `${who} ${verb.toLowerCase()} “${subject}”` : `${who} ${verb.toLowerCase()}`;
    return null;
}

// ONE ENTRY PER ACTION. A change to a node is written twice: once to the audit log
// and once to the node's own history, seconds apart. The history entry is kept
// (it carries the fields that changed); its audit twin goes.
const TWIN = { node_created: 'node_create', node_updated: 'node_update', node_deleted: 'node_delete', node_restored: 'node_restore' };
export function oncePerAction(entries) {
    const history = new Map();          // node id -> [timestamps of its history entries, by action]
    for (const e of entries) {
        if (e.source === 'audit_log' || !String(e.action || '').startsWith('node_') || !e.resource?.id) continue;
        const k = `${e.resource.id}|${e.action}`;
        history.set(k, [...(history.get(k) || []), new Date(e.timestamp).getTime()]);
    }
    return entries.filter((e) => {
        const twin = TWIN[e.action];
        if (e.source !== 'audit_log' || !twin || !e.resource?.id) return true;
        const t = new Date(e.timestamp).getTime();
        return !(history.get(`${e.resource.id}|${twin}`) || []).some((h) => Math.abs(h - t) < 10_000);
    });
}

// Consecutive entries with the same actor and action read as one run.
export function runsOf(entries) {
    const out = [];
    for (const e of entries) {
        const key = `${e.actor?.name}|${e.actor?.type}|${e.action}|${e.category}`;
        const last = out[out.length - 1];
        if (last && last.key === key) last.members.push(e);
        else out.push({ key, id: e.id, lead: e, members: [e] });
    }
    return out;
}
export function daysOf(runs) {
    const out = [];
    for (const r of runs) {
        const d = startOfDay(new Date(r.lead.timestamp)).getTime();
        const last = out[out.length - 1];
        if (last && last.day === d) last.runs.push(r); else out.push({ day: d, runs: [r] });
    }
    return out;
}

export const hm = (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const hms = (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
export const longWhen = (t) => new Date(t).toLocaleString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
export const actorName = (e) => {
    const name = e.actor?.name || e.actor?.username || 'Someone';
    // a token is never an app: it is named as what it is
    return e.actor?.type === 'token' ? `Token: ${name}` : name;
};
export const actorType = (e) => e.actor?.type || 'user';
export const viaApp = (e) => e.actor?.parent_app_name || e.actor?.app_name || null;
export const appOf = (e) => (actorType(e) === 'app' ? actorName(e) : actorType(e) === 'token' ? null : viaApp(e));

// Whether a row is the viewer's own doing: the Me and By me chips keep these, People the
// rest. On your own log a person's row that names no one is yours (older rows did not
// say); on a group's every row names its member.
export const isMine = (e, meId, inGroup) => actorType(e) === 'user'
    && ((!!meId && String(e.actor?.user_id) === String(meId)) || (!inGroup && !e.actor?.user_id));

// On a group ledger every row is somebody's doing: a person is You only when
// it is you, else a Member (or a User on your own ledger — someone you share with).
export function actorLabel(e, meId, inGroup) {
    const type = actorType(e);
    if (type === 'user') {
        if (meId && e.actor?.user_id && String(e.actor.user_id) === String(meId)) return 'You';
        if (!e.actor?.user_id && !inGroup) return 'You';
        return inGroup ? 'Member' : 'User';
    }
    return ONE_WORD[type] || 'System';
}

// A value as it was stored — a list stays a list, a string stays itself.
export const valueText = (v) => {
    if (v === undefined || v === null || v === '') return '—';
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
};
const SEVERITY = { warn: 'Warning', warning: 'Warning', error: 'Error', critical: 'Critical' };
export const severityOf = (e) => (e.severity && e.severity !== 'info' ? SEVERITY[e.severity] || e.severity : null);

// A detail as a person reads it: "Response time · 132 ms", not "response_time_ms · 132".
export function detailOf(key, value) {
    if (key === 'response_time_ms') return ['Response time', `${value} ms`];
    if (key === 'status_code') return ['Status', String(value)];
    if (key === 'ip_address' || key === 'ip') return ['From', String(value)];
    const words = key.replace(/_/g, ' ');
    return [words[0].toUpperCase() + words.slice(1), typeof value === 'object' ? JSON.stringify(value) : String(value)];
}

// changed_fields arrives as {field: {old, new}} — or, masked to what an app
// may see, as a bare list of names. Both read as rows.
export function changesOf(changed) {
    if (!changed) return [];
    if (Array.isArray(changed)) return changed.map((f) => ({ field: String(f) }));
    if (typeof changed !== 'object') return [];
    return Object.entries(changed).map(([field, d]) => (
        d && typeof d === 'object' && !Array.isArray(d) && ('old' in d || 'new' in d)
            ? { field, old: d.old, new: d.new, diff: true }
            : { field, value: d }
    ));
}

// The one line under a run's summary: for whom, through what, under which app.
export function metaOf(e) {
    const parts = [];
    if (e.actor?.acting_username) parts.push(`for ${e.actor.acting_username}`);
    const realm = e.actor?.privacy_realm;
    if (realm) parts.push(`through ${realm}${e.actor?.exposure_profile ? ` / ${e.actor.exposure_profile}` : ''}`);
    const via = viaApp(e);
    if (via && !HOLDS_ITS_OWN.has(actorType(e))) parts.push(`under ${via}`);
    const t = e.resource?.title || e.resource?.label;
    if (t && !(e.summary || '').includes(t)) parts.push(t);
    return parts.length ? parts.join(' · ') : null;
}

// ── the address ─────────────────────────────────────────────────────────────
// FILTERS LIVE IN THE ADDRESS, so a filtered ledger is a link and survives a
// reload: view · cat · entity · via · outcome · range · from · to · q. Only what
// you CHOSE is written; the defaults are left out.
export const FILTER_KEYS = ['view', 'cat', 'entity', 'via', 'outcome', 'range', 'from', 'to', 'q'];
// What did not go through (?outcome=): an app's call its contract refused, and
// anything else that failed. The server narrows the whole ledger to those.
export const OUTCOMES = { refused: 'Refused', failed: 'Failed' };
export const OUTCOME_IDS = Object.keys(OUTCOMES);
export const outcomeWord = (set) => inOrder(set, OUTCOME_IDS).map((k, i) => (i ? OUTCOMES[k].toLowerCase() : OUTCOMES[k])).join(' or ');
export const isoDay = (d) => { const x = new Date(d); return new Date(x.getTime() - x.getTimezoneOffset() * 60_000).toISOString().slice(0, 10); };
const dayAt = (s, time) => { if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null; const d = new Date(`${s}T${time}`); return Number.isNaN(d.getTime()) ? null : d; };
const listParam = (params, k, valid) => {
    const v = params.get(k);
    if (!v) return null;
    const got = v.split(',').filter((x) => x && (!valid || valid.includes(x)));
    return got.length ? new Set(got) : null;
};
export const hasExplicitFilters = (params) => FILTER_KEYS.some((k) => params.get(k));

// A link that names one entry — ?audit= (a notification's "record of the
// event") or ?open= (Apps › Recent, an inspector's door) — opens the whole
// ledger when nothing else is asked for: every kind, all time, so an entry
// older than a week, or of a kind outside your defaults, is there. ?open= used
// to keep the 7-day default, and most of what Apps › Recent named was "not in
// these filters".
//
// ?outcome= (an app's "See why") asks for what did not go through, whatever its
// kind: with no kinds named, every kind, so a person's own default kinds never
// hide a failure the link promised.
export function filtersFromUrl(params) {
    const widen = !!(params.get('audit') || params.get('open')) && !hasExplicitFilters(params);
    const outcome = listParam(params, 'outcome', OUTCOME_IDS);
    const v = params.get('view');
    const r = params.get('range');
    return {
        view: [...VIEWS, ...GROUP_VIEWS].some((x) => x.id === v) ? v : 'all',
        categories: listParam(params, 'cat', KIND_IDS) || (widen || outcome ? new Set(KIND_IDS) : null),
        entities: listParam(params, 'entity', ENTITY_TYPES),
        via: listParam(params, 'via'),
        outcome,
        range: RANGES.some((x) => x.id === r) ? r : (widen ? 'all' : 'week'),
        from: dayAt(params.get('from'), '00:00:00'),
        to: dayAt(params.get('to'), '23:59:59'),
        search: params.get('q') || '',
    };
}

// ── doors: where the things an entry names live on the map ───────────────────
const RESOURCE_DOORS = {
    node: (id) => ['graph', null, id, 'The node'],
    relationship: (id) => ['graph', null, id, 'The connection'],
    proposal: (id) => ['graph', 'review', id, 'The proposal'],
    exposure_profile: (id) => ['realms', null, id, 'The realm'],
    privacy_realm: (id) => ['realms', null, id, 'The realm'],
    plugin: (id) => ['plugins', null, id, 'The plugin'],
    plugin_run: (id) => ['plugins', 'runs', id, 'The run'],
    automation: (id) => ['plugins', 'automations', id, 'The automation'],
    third_party: (id) => ['apps', null, id, 'The app'],
};

// The thing an entry happened to, as a door: { label, href } or null.
export function resourceDoor(e, groupId) {
    const r = e.resource || {};
    const build = RESOURCE_DOORS[r.type];
    if (!build || !r.id) return null;
    const [section, page, id, word] = build(r.id);
    return { label: r.title || r.label || word, word, href: `${pathFor(section, page, groupId)}?open=${encodeURIComponent(id)}` };
}

// THE RUN an entry belongs to, as a door to it on Runs: a plugin's run, and
// each read it made, carry its `run_id`. Null for anything else.
export function runDoor(e, groupId) {
    const d = e?.details && typeof e.details === 'object' ? e.details : {};
    const id = d.run_id || (e?.resource?.type === 'plugin_run' ? e.resource.id : null);
    if (!id || e?.redacted) return null;
    const plugin = d.plugin_name || (e.resource?.type === 'plugin' ? e.resource.title || e.resource.label : null);
    return {
        label: 'The run',
        word: plugin ? `${plugin}’s run, what it made and left out` : 'What it made and left out',
        href: `${pathFor('plugins', 'runs', groupId)}?open=${encodeURIComponent(id)}`,
    };
}

// Who acted, as a door — the person, the app, the plugin or automation — or
// null when it is you, the system, or someone the map has no page for.
export function actorDoor(e, groupId, meId) {
    const a = e.actor || {};
    const at = (section, page, id) => `${pathFor(section, page, groupId)}${id ? `?open=${encodeURIComponent(id)}` : ''}`;
    switch (actorType(e)) {
        case 'app': return a.app_id || a.parent_app_id ? at('apps', null, a.app_id || a.parent_app_id) : null;
        // a token's contract, in Contracts (its key there is token:<its own id>:)
        case 'token': return a.app_id ? at('realms', 'profiles', `token:${a.app_id}:`) : at('realms', 'profiles');
        // a COSM agent's contract, in Contracts; the public page, where you set it
        case 'cosm_agent': return a.user_id ? at('realms', 'profiles', `cosm_agent:${a.user_id}:`) : at('realms', 'profiles');
        case 'public': return groupId ? null : at('people', 'public');
        case 'sub_entity': return at('apps', 'agents');
        case 'plugin': return a.plugin_id ? at('plugins', null, a.plugin_id) : at('plugins');
        case 'automation': return a.automation_id ? at('plugins', 'automations', a.automation_id) : at('plugins', 'automations');
        case 'group': return !groupId && a.group_id ? `${pathFor('groups', null, null)}?open=${encodeURIComponent(a.group_id)}` : null;
        case 'user':
            if (!a.user_id || String(a.user_id) === String(meId)) return null;
            return at('people', groupId ? 'members' : null, a.user_id);
        default: return null;
    }
}

// The parent app a delegate acted under, as a door.
export function parentDoor(e, groupId) {
    const a = e.actor || {};
    if (HOLDS_ITS_OWN.has(actorType(e)) || !a.parent_app_id) return null;
    return `${pathFor('apps', null, groupId)}?open=${encodeURIComponent(a.parent_app_id)}`;
}

// ── the inbox ───────────────────────────────────────────────────────────────
export const LANES = [
    { id: 'queue', label: 'To do' },
    { id: 'feed', label: 'Feed' },
    { id: 'security', label: 'Security' },
    { id: 'all', label: 'Everything' },
];
export const LANE_EMPTY = {
    queue: 'Nothing is waiting on you.',
    feed: 'Nothing has happened here yet. Shares, imports and automation runs arrive here.',
    security: 'No sign-ins, credential changes or grant changes recorded.',
    all: 'Your inbox is empty.',
};
export const LANE_WORD = { queue: 'To do', feed: 'Feed', security: 'Security' };

// The Mac's wording for an answer: an ellipsis where a screen follows.
export function answerLabel(row, action) {
    if (action.id === 'accept' && row.subject_type === 'group_invitation') return 'Join';
    if (action.id === 'accept') return 'Accept';
    if (action.id === 'review') return 'Review';
    return action.label;
}

// The old param names a notification's address still speaks, onto ?open=.
const OPEN_ALIASES = ['proposal', 'question', 'plugin', 'automation', 'node'];

// WHERE A NOTIFICATION OPENS, ON THE MAP. The shared resolver
// (@paradigm/notifications hrefFor) still speaks the addresses Nucleus had
// before the map (/graph?node=, /applications?ep=, /activity?audit=…). Each is
// carried onto the page that now holds it here — legacy.js does most of it —
// so "Go to it" never takes a redirect, and a group's "record of the event"
// lands on that group's Log, not a loop through the old ledger address.
export function mapHref(to) {
    if (!to?.url) return null;
    const [path, search = ''] = to.url.split('?');
    const audit = new URLSearchParams(search).get('audit');
    const g = path.match(/^\/group\/([^/]+)\//);
    if (/^(\/group\/[^/]+)?\/activity\/?$/.test(path) && audit) {
        return { ...to, url: `${pathFor('activity', null, g ? decodeURIComponent(g[1]) : null)}?audit=${encodeURIComponent(audit)}` };
    }
    if (/^(\/group\/[^/]+)?\/people\/?$/.test(path) && new URLSearchParams(search).get('people') === 'requests') {
        return { ...to, url: pathFor('people', 'friends', null) };
    }
    const moved = legacyTarget(path, search ? `?${search}` : '') || to.url;
    const [p2, s2 = ''] = moved.split('?');
    const q = new URLSearchParams(s2);
    OPEN_ALIASES.forEach((k) => { if (q.get(k) && !q.get('open')) { q.set('open', q.get(k)); q.delete(k); } });
    const rest = q.toString();
    return { ...to, url: rest ? `${p2}?${rest}` : p2 };
}
